import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import { ReactComponent as CloseIcon } from 'assets/icons/close_circle.svg';
import { ReactComponent as LinkIcon } from 'assets/icons/link.svg';
import { ChangeEvent, FocusEvent, FormEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidUrl, withProtocol } from 'utils/url';
import { PAGE_LINK_PREFIX } from 'widgets/sdk/widgetSdk.config';

import { useToast } from '../Toast';

interface AddLinkButtonProps {
  isActive: boolean;
  value?: string;
  text?: string;
  onChange: (url: string | null) => void;
  onMouseDown?: (event: MouseEvent<HTMLButtonElement>) => void;
  pageOrder: string[];
  activePage: string;
}

export const AddLinkButton = ({
  value,
  isActive,
  onChange,
  onMouseDown,
  text,
  pageOrder,
  activePage,
}: AddLinkButtonProps) => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'hyperLink' });

  const [link, setLink] = useState(value || '');

  // TRUE if user entered invalid url
  const [hasLinkError, setHasLinkError] = useBoolean(false);

  // Form popover state
  const [isOpen, setIsOpen] = useBoolean(false);

  // Page dropdown state
  const [isPagePopoverOpen, setIsPagePopoverOpen] = useBoolean(false);

  const toast = useToast();

  const linkInputRef = useRef<HTMLInputElement>(null);

  // Ref to store selected page link in the form "page://pageId" if a page is selected
  const selectedPageLinkRef = useRef<string | null>(null);

  const label = t('label');
  const textLabel = t('textLabel');
  const urlLabel = t('urlLabel');
  const saveChangesLabel = t('saveChanges');
  const clearUrlLabel = t('clearUrlLabel');
  const linkAddedToast = t('linkAdded');
  const linkUpdatedToast = t('linkUpdated');
  const urlErrorMsg = t('urlError');

  const showToast = () => {
    if (!value) {
      toast({
        title: linkAddedToast,
        status: 'success',
      });
    } else {
      toast({
        title: linkUpdatedToast,
        status: 'success',
      });
    }
  };

  // Propagate changes to parent component
  const onChangeLink = () => {
    if (link === '') {
      setHasLinkError.off();
      showToast();
      return onChange(link);
    }
    if (link === value) {
      setHasLinkError.off();
      return;
    }

    // If a page was selected, use that as the link value, not the display value
    if (selectedPageLinkRef.current) {
      setHasLinkError.off();
      showToast();
      return onChange(selectedPageLinkRef.current);
    }

    const fullUrl = withProtocol(link!);
    // When a link is added to an image - set isDecorative to false so that it will be read by screen readers
    if (isValidUrl(fullUrl)) {
      setHasLinkError.off();
      showToast();
      return onChange(fullUrl);
    } else {
      setHasLinkError.on();
    }
  };

  // Handle input change
  const onChangeLinkEventHandler = (e: ChangeEvent<HTMLInputElement>) => {
    // Close page number dropdown when user starts typing
    if (e.target.value) {
      setIsPagePopoverOpen.off();
    } else {
      // Open if input was cleared
      setIsPagePopoverOpen.on();
    }
    selectedPageLinkRef.current = null;
    setLink(e.target.value);
  };

  // Clear input field
  const onClearValue = () => {
    setLink('');
    selectedPageLinkRef.current = null;
    linkInputRef.current?.focus();
  };

  // Page dropdown selection handler
  const setPageLink = (pageUrl: string) => {
    setIsPagePopoverOpen.off();

    const pageIdx = pageOrder.indexOf(pageUrl.replace(PAGE_LINK_PREFIX, ''));
    if (pageIdx === -1) {
      selectedPageLinkRef.current = '';
      setLink('');
      return;
    }

    selectedPageLinkRef.current = pageUrl;
    setLink(t('pageLabel', { pageNumber: pageIdx + 1 }));
  };

  // On blur of the form, close the page dropdown
  const handleBlurUrlForm = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget?.contains(e.relatedTarget as Node)) {
      setIsPagePopoverOpen.off();
    }
  };

  // Formm submit handler, propagate changes and close popover
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onChangeLink();
    setIsOpen.off();
  };

  // Sync input value with prop value when prop value changes
  // Handles setting page display value when page url is passed in
  const syncLink = useCallback(() => {
    let displayValue = value || '';

    // Handle page:// links
    if (displayValue?.startsWith(PAGE_LINK_PREFIX)) {
      const pageId = displayValue.replace(PAGE_LINK_PREFIX, '');
      const pageIdx = pageOrder.indexOf(pageId);
      if (pageIdx !== -1) {
        displayValue = t('pageLabel', { pageNumber: pageIdx + 1 });
        selectedPageLinkRef.current = value || '';
      } else {
        // Page doesn't exist for some reason, clear the value
        displayValue = '';
      }
    }

    setLink(displayValue);
  }, [value, pageOrder, t]);

  const handleClose = () => {
    setIsOpen.off();
    syncLink();
  };

  // Sync input value with prop value when prop value changes
  useEffect(() => {
    syncLink();
  }, [syncLink]);

  return (
    <Popover isLazy placement='bottom-start' isOpen={isOpen} onClose={handleClose}>
      <Tooltip hasArrow placement='bottom' label={label} bg='black'>
        <Box>
          <PopoverTrigger>
            <IconButton
              isActive={isActive}
              onMouseDown={onMouseDown}
              onClick={setIsOpen.toggle}
              size='sm'
              aria-label={label}
              variant='icon-btn-toolbar'
              icon={<LinkIcon />}
              data-testid='add-link-trigger'
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>
      <PopoverContent>
        <form onSubmit={onSubmit}>
          <Flex direction='column' p={4} gap={4}>
            {text !== undefined && (
              <Flex direction='column' gap={2}>
                <FormLabel fontSize='xs' color='font.500' htmlFor='toolbar-link-text' mb={0}>
                  {textLabel}
                </FormLabel>
                <Input
                  id='toolbar-link-text'
                  size='sm'
                  border='1px solid'
                  borderColor='gray.300'
                  value={text}
                  _disabled={{
                    color: 'font.300',
                    opacity: 1,
                  }}
                  isDisabled
                />
              </Flex>
            )}
            <FormControl isInvalid={hasLinkError}>
              <Flex direction='column' gap={2} position='relative' onBlur={handleBlurUrlForm}>
                <FormLabel fontSize='xs' color='font.500' htmlFor='toolbar-link-input' mb={0}>
                  {urlLabel}
                </FormLabel>
                <InputGroup>
                  <Input
                    id='toolbar-link-input'
                    size='sm'
                    border='1px solid'
                    borderColor='gray.300'
                    value={link}
                    onChange={onChangeLinkEventHandler}
                    onFocus={setIsPagePopoverOpen.on}
                    ref={linkInputRef}
                    data-testid='toolbar-link-input'
                    autoComplete='off'
                  />
                  {link && (
                    <InputRightElement h={'full'}>
                      <IconButton
                        size='xs'
                        borderRadius='full'
                        variant='unstyled'
                        onClick={onClearValue}
                        aria-label={clearUrlLabel}
                        icon={
                          <Center>
                            <CloseIcon stroke='var(--vg-colors-outline-gray)' />
                          </Center>
                        }
                        data-testid='clear-icon-search-button'
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
                {isPagePopoverOpen && (
                  <PagePopoverContent onSelectPage={setPageLink} pageOrder={pageOrder} currentPageId={activePage} />
                )}
                <FormErrorMessage fontSize='xs'>{urlErrorMsg}</FormErrorMessage>
              </Flex>
              <Flex justify='flex-end'>
                <Button type='submit' size='sm' mt={2} variant='action' data-testid='toolbar-link-submit-btn'>
                  {saveChangesLabel}
                </Button>
              </Flex>
            </FormControl>
          </Flex>
        </form>
      </PopoverContent>
    </Popover>
  );
};
AddLinkButton.displayName = 'Link';

interface PagePopoverContentProps {
  pageOrder: string[];
  currentPageId: string;

  // Callback to handle page selection - passes in page url of the form page://PAGE_ID
  onSelectPage: (pageUrl: string) => void;
}

const PagePopoverContent = ({ pageOrder, currentPageId, onSelectPage }: PagePopoverContentProps) => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'hyperLink' });
  const header = t('documentPagesLabel');
  const handleSelectPage = (pageId: string) => {
    onSelectPage(`${PAGE_LINK_PREFIX}${pageId}`);
  };

  if (pageOrder.length <= 1) {
    return null;
  }

  return (
    <Box
      position='absolute'
      p={2}
      top='100%'
      w='full'
      borderRadius='base'
      mt={2}
      bg='white'
      boxShadow='xs'
      zIndex='popover'
      data-testid='page-number-selection-dropdown'
    >
      <Text fontSize='xs' color='font.500' fontWeight='semibold'>
        {header}
      </Text>
      <Flex direction='column' gap={2} mt={2} maxH='10rem' overflow='auto'>
        {pageOrder.map((pageId, index) => {
          const isCurrentPage = currentPageId === pageId;
          return (
            <Button
              key={pageId}
              size='xs'
              variant='ghost'
              color='font.500'
              isDisabled={isCurrentPage}
              onClick={() => {
                handleSelectPage(pageId);
              }}
              justifyContent='flex-start'
              alignItems='center'
              gap={2}
              fontWeight='normal'
              fontSize='xs'
              p={2}
              data-testid='page-number-selection-button'
            >
              {t('pageLabel', { pageNumber: index + 1 })}
            </Button>
          );
        })}
      </Flex>
    </Box>
  );
};
